/**
 * UI-3d Task 3 — DncImportDialog.
 *
 * A paste-and-batch wizard for bulk-adding Do-Not-Call entries (spec §3.6,
 * §3.7). The admin pastes E.164 phone numbers — one per line — picks a source
 * and an optional reason, and the dialog POSTs each VALID line sequentially to
 * `POST /dnc`, rendering a live progress bar and a final per-row result table.
 *
 * Backend contract (apps/api/src/routes/dnc.ts — `AddDncSchema`)
 * --------------------------------------------------------------
 *   POST /dnc   body: { phone, phoneE164, reason?, source? }
 * `phone` and `phoneE164` are the same validated E.164 value (see DncDrawer).
 * No `expiresAt` is sent — the import tool creates permanent entries.
 *
 * Why useState (not ZodForm)
 * --------------------------
 * This is a batch tool with genuine sequential UI state (`pasted`, `source`,
 * `reason`, `progress`, `results`), not a structured validated form. Plan
 * §0.F's ZodForm rule targets form drawers, so plain `useState` is correct.
 *
 * Invalid lines
 * -------------
 * A line that fails `isValidE164` becomes a failed result with "Invalid E.164"
 * WITHOUT an API call — only valid lines are POSTed.
 *
 * Reset on reopen
 * ---------------
 * `Dialog` keeps its children mounted across an open/close cycle, so state is
 * cleared by an effect keyed on `open` — every fresh open starts blank.
 *
 * Guards
 * ------
 * - Double-submit: `importing` disables the Import button; a synchronous
 *   `inFlightRef` closes the click→re-render race.
 * - Unmount: the batch is a sequence of `await`s, so `mountedRef` guards every
 *   `setState` that runs after an awaited boundary.
 */
import { useEffect, useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { ProgressBar } from 'primereact/progressbar'
import { api } from '../../../api/client.js'
import { isValidE164 } from '../../../utils/e164.js'
import { SOURCE_OPTIONS } from './DncDrawer.js'

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

export interface DncImportDialogProps {
  open: boolean
  onClose: () => void
  /** Called once after the batch completes so the parent refetches the list. */
  onImported: () => void
}

type DncSource = (typeof SOURCE_OPTIONS)[number]['value']

interface ImportResult {
  phone: string
  ok: boolean
  error?: string
}

// Reuse the single 5-option source list from DncDrawer; the Dropdown wants
// plain `{ label, value }` objects.
const SOURCE_DROPDOWN_OPTIONS = SOURCE_OPTIONS.map((o) => ({
  label: o.label,
  value: o.value,
}))

/** Split pasted text into trimmed, non-empty lines. */
function parseLines(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DncImportDialog({
  open,
  onClose,
  onImported,
}: DncImportDialogProps): JSX.Element {
  const [pasted, setPasted] = useState('')
  const [source, setSource] = useState<DncSource>('tenant')
  const [reason, setReason] = useState('')
  const [progress, setProgress] = useState(0)
  const [results, setResults] = useState<ImportResult[] | null>(null)
  const [importing, setImporting] = useState(false)

  /** Synchronous double-submit guard — set before any `await`. */
  const inFlightRef = useRef(false)
  /** False once unmounted — guards `setState` after an awaited boundary. */
  const mountedRef = useRef(true)
  useEffect(() => () => {
    mountedRef.current = false
  }, [])

  // Dialog keeps children mounted across open/close — clear state on each
  // fresh open so the wizard never shows stale paste / results.
  useEffect(() => {
    if (open) {
      setPasted('')
      setSource('tenant')
      setReason('')
      setProgress(0)
      setResults(null)
      setImporting(false)
      inFlightRef.current = false
    }
  }, [open])

  const lines = parseLines(pasted)

  // --- Import flow ---------------------------------------------------------

  async function importBatch(): Promise<void> {
    // Synchronous re-entry guard closes the gap before `importing` re-renders.
    if (inFlightRef.current || lines.length === 0) return
    inFlightRef.current = true
    setImporting(true)
    setResults(null)
    setProgress(0)

    const collected: ImportResult[] = []
    for (const phone of lines) {
      if (!isValidE164(phone)) {
        collected.push({ phone, ok: false, error: 'Invalid E.164' })
      } else {
        try {
          await api.post('/dnc', {
            phone,
            phoneE164: phone,
            source,
            reason: reason || undefined,
          })
          collected.push({ phone, ok: true })
        } catch (err) {
          collected.push({
            phone,
            ok: false,
            error: err instanceof Error ? err.message : 'Failed to add entry',
          })
        }
      }
      if (mountedRef.current) {
        setProgress((collected.length / lines.length) * 100)
      }
    }

    if (mountedRef.current) {
      setResults(collected)
      setImporting(false)
    }
    inFlightRef.current = false
    // The successful rows changed the list — refetch even if some failed.
    onImported()
  }

  // --- Render --------------------------------------------------------------

  return (
    <Dialog
      visible={open}
      onHide={onClose}
      header="Import DNC List"
      style={{ width: '40rem' }}
      modal
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="dnc-import-textarea" className="text-sm font-medium">
            Paste E.164 phone numbers (one per line)
          </label>
          <InputTextarea
            id="dnc-import-textarea"
            aria-describedby="dnc-import-hint"
            rows={8}
            value={pasted}
            disabled={importing}
            onChange={(e) => setPasted(e.target.value)}
            placeholder={'+15551234567\n+15559998888'}
          />
          <p id="dnc-import-hint" className="text-xs text-gray-500">
            {lines.length} {lines.length === 1 ? 'number' : 'numbers'} to import
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="dnc-import-source" className="text-sm font-medium">
            Source
          </label>
          <Dropdown
            inputId="dnc-import-source"
            value={source}
            disabled={importing}
            options={SOURCE_DROPDOWN_OPTIONS}
            onChange={(e) => setSource((e.value as DncSource) ?? 'tenant')}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="dnc-import-reason" className="text-sm font-medium">
            Reason
          </label>
          <InputText
            id="dnc-import-reason"
            value={reason}
            disabled={importing}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional — applied to every imported number"
          />
        </div>

        {importing && (
          <div className="flex flex-col gap-1">
            <ProgressBar value={Math.round(progress)} />
          </div>
        )}

        {results != null && (
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Results</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="border-b border-gray-300 px-2 py-1 text-left font-medium">
                      Phone
                    </th>
                    <th className="border-b border-gray-300 px-2 py-1 text-left font-medium">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={`${r.phone}-${i}`}>
                      <td className="border-b border-gray-100 px-2 py-1">
                        {r.phone}
                      </td>
                      <td
                        className={`border-b border-gray-100 px-2 py-1 ${
                          r.ok ? 'text-green-700' : 'text-red-600'
                        }`}
                      >
                        {r.ok ? '✓ Added' : (r.error ?? 'Failed')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-2 flex justify-end gap-2">
          <Button label="Cancel" onClick={onClose} disabled={importing} />
          <Button
            label="Import"
            onClick={() => void importBatch()}
            disabled={importing || lines.length === 0}
          />
        </div>
      </div>
    </Dialog>
  )
}
